/**
 * De vaste a11y-teksten ín de Aurora-componenten — issue 294.
 *
 * LOKALE TOEVOEGING, niet in het package. Upstream hardcodeert deze teksten in
 * het Engels (`"Close"`, `"Dismiss"`, …) en biedt geen manier om ze te vervangen;
 * gemeld bij het DS-team, zie AURORA-NOTES.md.
 *
 * ## Waarom een context en geen prop per component
 *
 * De gewone vorm in deze map is een prop met een Engelse default (zie
 * `ComboboxProps.emptyText`). Dat past bij tekst die per aanroep verschilt. Deze
 * teksten verschillen niet: de sluitknop van een `Modal` heet overal hetzelfde.
 * Als prop zou elke aanroeper hem moeten meegeven — 42 bestanden met een `Modal`,
 * ~65 `Alert`s met `onDismiss` — en wie hem vergeet krijgt geen foutmelding, maar
 * Engels in een Nederlandse omgeving. Dat is precies hoe de `DataTable`-labels
 * negentien keer half werden ingevuld (issue 643).
 *
 * Hier gebeurt het dus op één plek: de app zet `AuroraLabelsProvider` één keer om
 * de root (`TranslatedAuroraLabels` in `shared/components/`), gevuld uit i18n.
 *
 * ## Waarom Aurora zelf geen `useTranslation()` doet
 *
 * Dan hangt de design-laag aan de vertaalbundel van de app; dat is bij `DataTable`
 * bewust andersom besloten. De context hoort bij Aurora, de Engelse defaults ook.
 * Zonder provider — een losse component-test, een ander project — werkt alles
 * zoals vóór deze wijziging.
 *
 * ## Waar een nieuwe tekst hoort
 *
 * - Verschilt hij per aanroep (een titel, een lege-lijst-tekst, de naam van een
 *   landmark): een prop.
 * - Is hij overal hetzelfde: hier, plus een sleutel in de `aurora`-groep van de
 *   locales. Heeft het component al een eigen label-bundel (`DataTableLabels`),
 *   dan daar.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface AuroraLabels {
  /** `Modal` — de sluitknop in de kop. */
  modalClose: string;
  /** `Alert` — de knop die de melding wegklikt. */
  alertDismiss: string;
  /** `Input variant="password"` — de oog-knop, als het wachtwoord verborgen is. */
  passwordShow: string;
  /** `Input variant="password"` — de oog-knop, als het wachtwoord zichtbaar is. */
  passwordHide: string;
  /** `Combobox` — het kruisje op een gekozen waarde. `{label}` = die waarde. */
  comboboxRemove: string;
}

/** De Engelse terugval — gelijk aan wat er vóór issue 294 hardgecodeerd stond. */
export const AURORA_DEFAULT_LABELS: Readonly<AuroraLabels> = {
  modalClose: 'Close',
  alertDismiss: 'Dismiss',
  passwordShow: 'Show password',
  passwordHide: 'Hide password',
  comboboxRemove: 'Remove {label}',
};

const AuroraLabelsContext = createContext<AuroraLabels>(AURORA_DEFAULT_LABELS);

export interface AuroraLabelsProviderProps {
  /** Wat je niet meegeeft, valt terug op `AURORA_DEFAULT_LABELS`. */
  labels: Partial<AuroraLabels>;
  children?: ReactNode;
}

export function AuroraLabelsProvider({ labels, children }: AuroraLabelsProviderProps) {
  // Gememoiseerd op de bundel: de provider staat om de hele app, dus een vers
  // object per render zou elke `Modal` en `Alert` mee laten renderen.
  const value = useMemo(() => ({ ...AURORA_DEFAULT_LABELS, ...labels }), [labels]);
  return <AuroraLabelsContext.Provider value={value}>{children}</AuroraLabelsContext.Provider>;
}

export function useAuroraLabels(): AuroraLabels {
  return useContext(AuroraLabelsContext);
}

/** Vult `{key}`-placeholders in — dezelfde conventie als de locale-bundels. */
export function formatLabel(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}
